import { action, computed, makeObservable, observable } from "mobx"
import { formatUnits } from "viem"

import type { TokenStore } from "@/domains/tokens/token-store"
import type { PoolIntervalItem } from "@/domains/validators/graphql-service/pools"
import { LcAddress } from "@/domains/core"
import { tickToPriceString, getUtcDayFeesUsd, getUtcDayVolumeUsd } from "@/domains/manual-cl-core"
import { CL_PRODUCT_COPY } from "@/domains/manual-cl-page/cl-product-copy"
import { ILiquidityPool } from "@/domains/pools"
import { ILiquidityPoolType } from "@/domains/pools/types"

import type { ManualClPoolStoreDAO } from "./types"
import type { SevenDayRangeAprResult } from "./utils/seven-day-range-apr"
import { sevenDayRangeApr } from "./utils/seven-day-range-apr"

/**
 * Thick-v2 pools carry dynamic swap fees on-chain, but the indexer still
 * materialises `feesUSD` at the nominal 1% tier (`feeTier = 10000`) on every
 * pool. Client-side fee metrics therefore derive from rolling volume × the
 * live fee tier (see `feesUsd24h`) rather than summing indexer `feesUSD`.
 */
const THICK_V2_INDEXER_NOMINAL_FEE_TIER = 10_000

export interface ManualClUsdMetricState {
  value: number
  status: "ready" | "unindexed-pair"
  sourceLabel: string
}

export interface ManualClTvlSparklineState {
  values: Array<number>
  status: "ready" | "unindexed-pair"
  label: string
}

export class ManualClPoolStore implements ILiquidityPool {
  address: LcAddress
  apiReserve0: bigint = 0n
  apiReserve1: bigint = 0n
  apiTvlUsd: number = 0
  decimals: number
  fee: number = 0
  feeTier: number = 0
  private hasLiveSwapFee: boolean = false
  tickSpacing: number = 0
  isStable: boolean
  onChainReserve0: bigint = 0n
  onChainReserve1: bigint = 0n
  onChainUserBalance: bigint = 0n
  onChainUserBalanceToken0: bigint = 0n
  onChainUserBalanceToken1: bigint = 0n
  tags: string[]
  token0: TokenStore
  token1: TokenStore
  volumeUsd: number = 0
  feesUsd: number = 0
  liquidityProviderCount: string = "0"
  /**
   * Pool-wide active (in-range) virtual liquidity at the current tick, as
   * reported by the indexer (`Pool.liquidity`). This is the denominator
   * every position divides into to work out its share of pool fees — the
   * same quantity the on-chain `IUniswapV3Pool.liquidity()` slot exposes,
   * and the same quantity the tick-distribution store computes by summing
   * `liquidityNet` up to the current tick. Stored here so per-position
   * forward-looking APR has a stable fallback even when the dedicated
   * tick-distribution store is scoped to a different pool (the cross-pool
   * my-positions list never loads full tick books).
   */
  liquidity: bigint = 0n
  currentTick: number | undefined = undefined
  /**
   * Current pool sqrtPriceX96 sourced from `IUniswapV3Pool.slot0()`.
   * Preferred over `currentTick` for bit-exact math — when undefined, callers
   * fall back to tick-derived sqrtRatios.
   */
  currentSqrtPriceX96: bigint | undefined = undefined
  /**
   * Token-denominated TVL pulled straight from the indexer. Used alongside
   * `tvlUsd` to render e.g. "1,200 ETH / 2.1M USDC" beneath a USD figure.
   */
  totalValueLockedToken0: number = 0
  totalValueLockedToken1: number = 0
  /**
   * Newest-first daily aggregates (up to seven). `poolDayData[0]` is the
   * current day in progress; `poolDayData[1]` is the previous completed day.
   * Drives `priceChangePct24h`, `tvlUsdDelta24hPct`, `tvlUsd7dAgo` and the
   * sparkline. Primary source for UTC calendar-day `volumeUsd24h` /
   * `feesUsd24h` via `poolDayData[0]`.
   */
  poolDayData: Array<PoolIntervalItem> = []
  /**
   * Newest-first hourly aggregates from the indexer. Used for UTC-day fallback
   * when today's daily bucket is not yet materialised, plus position
   * time-in-range sampling and range APR history — not summed wholesale for
   * headline 24h volume (sparse buckets span multiple days).
   */
  poolHourData: Array<PoolIntervalItem> = []

  constructor(data: ManualClPoolStoreDAO) {
    // Pool metadata
    this.address = data.address
    // displayName and displayType are computed properties (getters), not stored

    // Pool configuration
    this.decimals = data.decimals
    this.isStable = data.isStable

    // Financial data
    this.fee = data.fee
    this.feeTier = data.feeTier
    this.tickSpacing = data.tickSpacing ?? 64

    this.apiReserve0 = data.reserve0
    this.apiReserve1 = data.reserve1
    this.apiTvlUsd = data.tvlUsd

    // Token information
    this.token0 = data.token0
    this.token1 = data.token1

    // Metadata
    this.tags = data.tags

    // Additional data
    this.volumeUsd = data.volumeUsd
    this.feesUsd = data.feesUsd
    this.liquidityProviderCount = data.liquidityProviderCount
    this.liquidity = data.liquidity ?? 0n
    this.totalValueLockedToken0 = data.totalValueLockedToken0
    this.totalValueLockedToken1 = data.totalValueLockedToken1
    this.poolDayData = data.poolDayData ?? []
    this.poolHourData = data.poolHourData ?? []

    this.currentTick = data.currentTick
    this.currentSqrtPriceX96 = data.currentSqrtPriceX96

    // User values (phase 2 - all default to 0 for now)
    this.onChainReserve0 = 0n
    this.onChainReserve1 = 0n
    this.onChainUserBalance = 0n
    this.onChainUserBalanceToken0 = 0n
    this.onChainUserBalanceToken1 = 0n

    makeObservable(this, {
      apiReserve0: observable,
      apiReserve1: observable,
      apiTvlUsd: observable,
      fee: observable,
      feeTier: observable,
      tickSpacing: observable,
      onChainReserve0: observable,
      onChainReserve1: observable,
      onChainUserBalance: observable,
      onChainUserBalanceToken0: observable,
      onChainUserBalanceToken1: observable,
      tags: observable,
      token0: observable,
      token1: observable,
      volumeUsd: observable,
      feesUsd: observable,
      liquidity: observable,
      currentTick: observable,
      currentSqrtPriceX96: observable,
      totalValueLockedToken0: observable,
      totalValueLockedToken1: observable,
      poolDayData: observable,
      poolHourData: observable,

      // Actions
      setCurrentSqrtPriceX96: action,
      setCurrentTick: action,
      setCurrentFeeTier: action,
      setCurrentLiquidity: action,
      updateStaticData: action,

      // Computed properties
      displayApr: computed,
      rangeApr7d: computed,
      displayRangeApr7d: computed,
      rangeApr7dPct: computed,
      displayFee: computed,
      displayIncomePerEpoch: computed,
      displayMidPrice: computed,
      displayName: computed,
      displayType: computed,
      volumeUsd24hState: computed,
      feesUsd24hState: computed,
      volumeUsd24h: computed,
      feesUsd24h: computed,
      tvlUsd7dAgo: computed,
      tvlUsdDelta24hPct: computed,
      priceChangePct24h: computed,
      tokenDenomTvl: computed,
      tvlUsdSparkline: computed,
      tvlUsdSparklineState: computed,
      gaugeReserves: computed,
      gaugeTotalSupplyPercentage: computed,
      gaugeUserBalance: computed,
      gaugeUserBalancePercentage: computed,
      gaugeUserBalanceToken0: computed,
      gaugeUserBalanceToken1: computed,
      gaugeUserBalanceValueUSD: computed,
      gaugeValueUSD: computed,
      reserves: computed,
      tokens: computed,
      tvlUsd: computed,
      type: computed,
      userBalance: computed,
      userBalancePercentage: computed,
      userBalanceToken0: computed,
      userBalanceToken1: computed,
      userBalanceValueUSD: computed,
      walletValueUSD: computed,
    })
  }

  /**
   * Headline APR estimate derived from trailing-24-hour trading fees
   * annualised against current TVL. Historically this read yesterday's UTC
   * day only, which meant the number was frozen for 24 hours at a time;
   * `feesUsd24h` now returns a rolling sum of `PoolHourData`, so the APR
   * moves within a few minutes of fresh trading activity.
   *
   * Treated as an estimate — callers should prefix with "~" so we never
   * misrepresent it as a realised yield.
   *
   * Returns an empty string when TVL is zero so UI can render `—` instead
   * of a divide-by-zero Infinity.
   */
  get displayApr(): string {
    const tvl = this.tvlUsd
    if (!tvl || !isFinite(tvl) || tvl <= 0) return ""
    const dailyFees = this.feesUsd24h
    // No trading activity yesterday — return empty so UI renders `—`.
    // Previously we returned "0.00%", which falsely implied a meaningful
    // zero APR; in practice we just don't have enough data to compute it.
    if (!dailyFees || !isFinite(dailyFees) || dailyFees <= 0) return ""
    const apr = ((dailyFees * 365) / tvl) * 100
    if (!isFinite(apr)) return ""
    if (apr > 0 && apr < 0.01) return "<0.01%"
    return `${apr.toFixed(2)}%`
  }

  /**
   * Range-aware APR presented on the pool-discovery list. Uses the same
   * 7-day high/low the create-position flow draws as dashed reference
   * lines on the liquidity brush, paired with 7-day volume (annualised by
   * `365/7`) so the range window and the volume window match — unlike
   * `displayApr`, which annualises 24 hours of fees against an implicit
   * full-range deposit and is structurally too low for the tight ranges
   * CL LPs actually pick.
   *
   * Falls back to a symmetric ±2% range when the indexer has no usable
   * 7-day data (brand-new pool, stale indexer, schema misses). The result
   * carries a `source` field so the UI can label the tooltip honestly.
   */
  get rangeApr7d(): SevenDayRangeAprResult {
    const currentTick = this.currentTick
    const currentPriceRaw = this.displayMidPrice
    const currentPrice = currentPriceRaw ? parseFloat(currentPriceRaw) : undefined
    return sevenDayRangeApr({
      token0: this.token0,
      token1: this.token1,
      currentTick,
      currentPrice: Number.isFinite(currentPrice) ? currentPrice : undefined,
      feeTier: this.feeTier,
      tickSpacing: this.tickSpacing,
      tvlUsd: this.tvlUsd,
      poolDayData: this.poolDayData,
    })
  }

  /**
   * Pre-formatted string for direct UI consumption — e.g. `"141.23%"`.
   * Returns `""` when the helper could not produce a number, so callers
   * can render `—` without sentinel checking on the numeric field.
   */
  get displayRangeApr7d(): string {
    return this.rangeApr7d.formattedPct
  }

  /**
   * Numeric handle exposed for table sorting. Always returns a finite
   * number (zero when unavailable) so comparison is well-defined.
   */
  get rangeApr7dPct(): number {
    const pct = this.rangeApr7d.aprPct
    return Number.isFinite(pct) ? pct : 0
  }

  /**
   * UTC calendar-day (today) USD volume. Primary path reads
   * `poolDayData[0].volumeUSD`; when the daily bucket is not yet
   * materialised, sums hourly rows since UTC midnight. Never falls back to
   * yesterday's completed day.
   */
  get volumeUsd24h(): number {
    return getUtcDayVolumeUsd(this.poolDayData, this.poolHourData)
  }

  get volumeUsd24hState(): ManualClUsdMetricState {
    return {
      value: this.volumeUsd24h,
      status: this.volumeUsd24h > 0 ? "ready" : "unindexed-pair",
      sourceLabel: "24h",
    }
  }

  /**
   * UTC calendar-day (today) USD fees. Derived from {@link volumeUsd24h}
   * multiplied by the live swap-fee tier (`feeTier / 1_000_000`) because
   * thick-v2 indexer rows still record `feesUSD` at the nominal 1% tier even
   * when the pool's on-chain fee is dynamic. When volume is zero, indexed
   * fees from today's daily bucket (or hourly-since-midnight fallback) are
   * scaled from that nominal tier down to the live tier as a last resort.
   */
  get feesUsd24h(): number {
    const volume = this.volumeUsd24h
    if (volume > 0 && this.feeTier > 0) {
      return volume * (this.feeTier / 1_000_000)
    }

    const indexedFees = getUtcDayFeesUsd(this.poolDayData, this.poolHourData)
    if (indexedFees > 0 && this.feeTier > 0) {
      return indexedFees * (this.feeTier / THICK_V2_INDEXER_NOMINAL_FEE_TIER)
    }

    return indexedFees
  }

  get feesUsd24hState(): ManualClUsdMetricState {
    return {
      value: this.feesUsd24h,
      status: this.feesUsd24h > 0 ? "ready" : "unindexed-pair",
      sourceLabel: "24h",
    }
  }

  /**
   * TVL as of seven days ago — pulled from the last entry of `poolDayData`.
   * Used to compute 7-day TVL delta on the pool detail page.
   */
  get tvlUsd7dAgo(): number {
    const oldest = this.poolDayData[this.poolDayData.length - 1]
    return oldest?.tvlUSD ?? 0
  }

  /**
   * 24h TVL change expressed as a percentage of yesterday's close. Returns
   * `undefined` when yesterday's TVL is not available — render `—` in that
   * case rather than a misleading zero.
   */
  get tvlUsdDelta24hPct(): number | undefined {
    const yesterday = this.poolDayData[1]?.tvlUSD
    const today = this.tvlUsd
    if (!yesterday || yesterday <= 0) return undefined
    return ((today - yesterday) / yesterday) * 100
  }

  /**
   * 24h price change in percent using yesterday's OHLC close vs today's
   * close. Follows the token0-per-token1 orientation stored on the pool —
   * callers that prefer the inverse can flip the sign via
   * `-priceChangePct24h`.
   */
  get priceChangePct24h(): number | undefined {
    const yesterday = this.poolDayData[1]
    const today = this.poolDayData[0]
    const previousClose = yesterday?.close
    const latest = today?.close ?? today?.open
    if (!previousClose || previousClose <= 0 || !latest) return undefined
    return ((latest - previousClose) / previousClose) * 100
  }

  /**
   * Token-denominated TVL breakdown for the popover under the USD headline.
   */
  get tokenDenomTvl(): { token0: number; token1: number } {
    return {
      token0: this.totalValueLockedToken0,
      token1: this.totalValueLockedToken1,
    }
  }

  /**
   * Up-to-seven-point TVL series for the sparkline rendered in the pool
   * table. Ordered oldest-first so downstream chart libs can consume
   * directly.
   */
  get tvlUsdSparkline(): Array<number> {
    return [...this.poolDayData].reverse().map((d) => d.tvlUSD ?? 0)
  }

  get tvlUsdSparklineState(): ManualClTvlSparklineState {
    const values = this.tvlUsdSparkline.filter((value) => Number.isFinite(value) && value > 0)
    if (values.length >= 2) {
      return { values, status: "ready", label: "7D TVL" }
    }
    return { values, status: "unindexed-pair", label: "No pricing" }
  }

  get displayFee(): string {
    // fee is already a decimal percentage (e.g., 0.003 = 0.3%)
    // Format: show 4 decimals if last 2 digits are non-zero, otherwise show 2 decimals
    const feeValue = this.fee
    const fourDecimals = feeValue.toFixed(4)
    const lastTwoDigits = fourDecimals.slice(-2)

    // If last two digits are "00", use 2 decimals, otherwise use 4
    const formatted = lastTwoDigits === "00" ? feeValue.toFixed(2) : fourDecimals
    return `${formatted}%`
  }

  /**
   * Legacy v2 surface shows an "income per epoch" value under the APR
   * column. For manual-CL pools there is no gauge / emission epoch, so this
   * is always an empty string — the column branches on it and collapses.
   */
  get displayIncomePerEpoch(): string {
    return ""
  }

  /**
   * Pool mid-price formatted via `tickToPriceString` (TokenStore-aware,
   * bit-exact). Returns an empty string when `currentTick` is unavailable so
   * UI can render a placeholder instead of a wrong number.
   */
  get displayMidPrice(): string {
    if (this.currentTick === undefined) return ""
    try {
      return tickToPriceString({
        tick: this.currentTick,
        token0: this.token0,
        token1: this.token1,
        decimalPlaces: 6,
      })
    } catch {
      return ""
    }
  }

  get displayName(): string {
    return `${this.token0.symbol}/${this.token1.symbol}`
  }

  get displayType(): string {
    return `${CL_PRODUCT_COPY.v2.shortName} · spacing ${this.tickSpacing}`
  }

  get gaugeReserves(): Array<bigint> {
    // Phase 1: No gauges for manual CL
    return [0n, 0n]
  }

  get gaugeTotalSupplyPercentage(): number {
    // Phase 1: No gauges
    return 0
  }

  get gaugeUserBalance(): bigint {
    // Phase 1: No gauges
    return 0n
  }

  get gaugeUserBalancePercentage(): number {
    // Phase 1: No gauges
    return 0
  }

  get gaugeUserBalanceToken0(): bigint {
    // Phase 1: No gauges
    return 0n
  }

  get gaugeUserBalanceToken1(): bigint {
    // Phase 1: No gauges
    return 0n
  }

  get gaugeUserBalanceValueUSD(): number {
    // Phase 1: No gauges
    return 0
  }

  get gaugeValueUSD(): number {
    // Phase 1: No gauges
    return 0
  }

  get reserve0(): bigint {
    if (this.onChainReserve0 > 0n) {
      return this.onChainReserve0
    }
    if (this.apiReserve0 > 0n) {
      return this.apiReserve0
    }
    return 0n
  }

  get reserve1(): bigint {
    if (this.onChainReserve1 > 0n) {
      return this.onChainReserve1
    }
    if (this.apiReserve1 > 0n) {
      return this.apiReserve1
    }
    return 0n
  }

  get reserves(): Array<bigint> {
    return [this.reserve0, this.reserve1]
  }

  get tokens(): Array<TokenStore> {
    return [this.token0, this.token1]
  }

  get tvlUsd(): number {
    if (Number.isFinite(this.apiTvlUsd) && this.apiTvlUsd > 0) {
      return this.apiTvlUsd
    }
    return this.tokenDenominatedTvlUsd
  }

  get type(): ILiquidityPoolType {
    return "volatile" // CL pools are always volatile
  }

  get userBalance(): bigint {
    return this.onChainUserBalance
  }

  get userBalancePercentage(): number {
    // Phase 1: No user balance tracking
    return 0
  }

  get userBalanceToken0(): bigint {
    return this.onChainUserBalanceToken0
  }

  get userBalanceToken1(): bigint {
    return this.onChainUserBalanceToken1
  }

  get userBalanceValueUSD(): number {
    return (
      Number(this.token0.formatUnits(this.userBalanceToken0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.userBalanceToken1)) * this.token1.bestPrice
    )
  }

  get walletValueUSD(): number {
    return (
      Number(this.token0.displayBalance) * this.token0.bestPrice +
      Number(this.token1.displayBalance) * this.token1.bestPrice
    )
  }

  /**
   * Pool specific unit formatting (uses internal decimals)
   */
  formatUnits(wei: bigint): string {
    return formatUnits(wei, this.decimals)
  }

  formatUnitsAsNumber(wei: bigint): number {
    return Number(formatUnits(wei, this.decimals))
  }

  parseUnits(wei: string): bigint {
    return BigInt(wei) // For now, assume wei is already in correct format
  }

  /**
   * Update static data from API (useful for refreshing from GraphQL)
   */
  updateStaticData(data: Partial<ManualClPoolStoreDAO>): void {
    if (!this.hasLiveSwapFee && data.fee !== undefined) this.fee = data.fee
    if (!this.hasLiveSwapFee && data.feeTier !== undefined) this.feeTier = data.feeTier
    if (data.tickSpacing !== undefined) this.tickSpacing = data.tickSpacing
    if (data.reserve0 !== undefined) this.apiReserve0 = data.reserve0
    if (data.reserve1 !== undefined) this.apiReserve1 = data.reserve1
    if (data.tvlUsd !== undefined) this.apiTvlUsd = data.tvlUsd
    if (data.tags !== undefined) this.tags = data.tags
    if (data.volumeUsd !== undefined) this.volumeUsd = data.volumeUsd
    if (data.feesUsd !== undefined) this.feesUsd = data.feesUsd
    if (data.liquidityProviderCount !== undefined) this.liquidityProviderCount = data.liquidityProviderCount
    if (data.liquidity !== undefined) this.liquidity = data.liquidity
    if (data.currentTick !== undefined) this.currentTick = data.currentTick
    if (data.currentSqrtPriceX96 !== undefined) this.currentSqrtPriceX96 = data.currentSqrtPriceX96
    if (data.totalValueLockedToken0 !== undefined) this.totalValueLockedToken0 = data.totalValueLockedToken0
    if (data.totalValueLockedToken1 !== undefined) this.totalValueLockedToken1 = data.totalValueLockedToken1
    if (data.poolDayData !== undefined) this.poolDayData = data.poolDayData
    if (data.poolHourData !== undefined) this.poolHourData = data.poolHourData
  }

  /**
   * Action-equivalent setter for slot0() refreshes.
   * Orchestrator writes here on every OrchestratedQuery execution tick.
   */
  setCurrentSqrtPriceX96(value: bigint | undefined): void {
    this.currentSqrtPriceX96 = value
  }

  setCurrentTick(tick: number | undefined): void {
    this.currentTick = tick
  }

  setCurrentFeeTier(feeTier: number): void {
    if (!Number.isFinite(feeTier) || feeTier <= 0) return
    this.hasLiveSwapFee = true
    this.feeTier = feeTier
    this.fee = feeTier / 10_000
  }

  /**
   * Apply a fresh `Pool.liquidity` snapshot from the indexer. Called by the
   * position orchestrator (which receives this value via the nested
   * `Position.pool` fragment on every `getMyPositions` sync) and by the
   * main pool orchestrator via `updateStaticData`. Zero / non-finite
   * inputs are coerced to `0n` so a bad snapshot can't poison per-position
   * APR calculations that divide by this value.
   */
  setCurrentLiquidity(liquidity: bigint | undefined): void {
    this.liquidity = liquidity !== undefined && liquidity >= 0n ? liquidity : 0n
  }

  private get tokenDenominatedTvlUsd(): number {
    const token0Usd = this.totalValueLockedToken0 * this.token0.bestPrice
    const token1Usd = this.totalValueLockedToken1 * this.token1.bestPrice
    if (!Number.isFinite(token0Usd) || !Number.isFinite(token1Usd)) return 0
    const tvl = token0Usd + token1Usd
    return tvl > 0 ? tvl : 0
  }
}
